import { OrgAddressResponse } from './../../entities/organization/OrgResponse';
import { CompactOrderResponse, OrderEstimateStatus, OrderResponse, OrderStatus, OrderTermResponse } from '@business/entities/order';
import { InvoiceSettingsResponse } from '@business/entities/TransactionInvoice';
import { SettingsConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { OrderShippingOption } from './../../entities/order/OrderResponse';
import { OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import { OrderStageType } from './../../entities/order/OrderStageResponse';
import { Formatter } from '@core/utils/formatter';

export function getStatusTitle(status: OrderStatus | OrderEstimateStatus | OrderShipmentStatus) {
  switch (status) {
    case OrderStatus.open:
      return 'Open';
    case OrderStatus.completed:
      return 'Completed';
    case OrderStatus.canceled:
      return 'Canceled';
    case OrderShipmentStatus.acknowledged:
      return 'Acknowledged';
    case OrderShipmentStatus.shipped:
      return 'Shipped';
    case OrderShipmentStatus.delivered:
      return 'Delivered';
    case OrderShipmentStatus.opened:
      return 'Opened';
  }
  return 'Unknown';
}

export function allStatuses() {
  return [OrderStatus.open, OrderStatus.completed, OrderStatus.canceled];
}

export function hasShippingInfo(type: OrderStageType) {
  return R.includes([OrderStageType.acknowledge, OrderStageType.delivered, OrderStageType.shipped], type);
}
export function allShippingOptions() {
  return [OrderShippingOption.ready, OrderShippingOption.complete];
}

export function getShippingOptionTitle(status: OrderShippingOption) {
  switch (status) {
    case OrderShippingOption.ready:
      return 'Ship as Ready';
    case OrderShippingOption.complete:
      return 'Ship as Complete';
  }
  return 'Unknown';
}

export function getShipmentStatusTitle(status: OrderShipmentStatus) {
  switch (status) {
    case OrderShipmentStatus.acknowledged:
      return 'Acknowledged';
    case OrderShipmentStatus.shipped:
      return 'Shipped';
    case OrderShipmentStatus.opened:
      return 'Opened';
    case OrderShipmentStatus.delivered:
      return 'Delivered';
  }
  return 'Unknown';
}

export function shipmentStatuses() {
  return [OrderShipmentStatus.opened, OrderShipmentStatus.acknowledged, OrderShipmentStatus.shipped, OrderShipmentStatus.delivered];
}

export function canCompleteIt(order: OrderResponse | CompactOrderResponse, lines: string[] = []) {
  const poItems = R.flatMap(order.purchaseOrders, po => po.items).filter(
    i => R.includes(lines, i.id.toString()) && i.status !== OrderShipmentStatus.delivered,
  );
  if (order.stage.type === OrderStageType.delivered) {
    return poItems.length === 0;
  }
  return false;
  return poItems.length === 0 ? true : false;
}

export function getStatusClass(status: OrderStatus) {
  switch (status) {
    case OrderStatus.open:
      return 'badge-soft-primary';
    case OrderStatus.completed:
      return 'badge-soft-success';
    case OrderStatus.canceled:
      return 'badge-soft-danger';
  }
}

export function getItemStatusClass(status: string) {
  switch (status) {
    case 'closed':
    case 'open':
      return 'badge-primary';
    case 'accepted':
      return 'badge-success';
    case 'rejected':
      return 'badge-danger';
    case 'pending':
      return 'badge-warning';
  }
}

export function getApprovalActionTitle(status: string) {
  switch (status) {
    case 'noCreateInvoice':
      return "Don't create invoice";
    case 'createInvoice':
      return 'Create and Save invoice';
    case 'createSendInvoice':
      return 'Create and Send invoice';
  }
  return status;
}

export function isUMEnabled() {
  const orderSetting = SettingsConfig?.current()?.tm;
  if (orderSetting?.isUMEnabled) {
    return true;
  }

  return false;
}

export function isNameEnabled() {
  const orderSetting = SettingsConfig?.current()?.tm;
  if (orderSetting?.isNameEnabled) {
    return true;
  }
  return false;
}

export function getSettingAddressString(address: InvoiceSettingsResponse | OrgAddressResponse, joinBy = '\n') {
  return R.compact([
    address.address,
    `${address.city}, ${address.state} ${address.postal}`,
    address.country,
    Formatter.formatPhone(address.phone, (address as OrgAddressResponse)?.extra?.countryCode || address.country),
  ]).join(joinBy);
}

export function getDueDateByTerm(term: OrderTermResponse, date: number) {
  let momentDate = toMoment(date).add(term?.fixedDays || 0, 'days');
  if (term?.dayOfMonth && term.dayOfMonth > 0) {
    momentDate = momentDate.set('date', term.dayOfMonth === 31 ? momentDate.daysInMonth() : term.dayOfMonth);
  }

  if (term?.dayOfDueDate && term?.dayOfMonth && term.dayOfDueDate > 0 && momentDate.diff(toMoment(date), 'days') < term.dayOfDueDate) {
    momentDate = momentDate.add(term?.dayOfDueDate || 0, 'days');
    momentDate = momentDate.set('date', term?.dayOfMonth);
  }

  return momentDate.unix();
}
